/**
 * Base abstract error class for all xJet-specific errors
 *
 * Extends the native `Error` class with improved stack trace handling
 * and serialization capabilities for better error reporting.
 *
 * @remarks
 * This class serves as the foundation for all custom error types in the xJet framework.
 * It ensures consistent error behavior, proper prototype chain setup, and serialization
 * to support error logging and transmission across boundaries.
 *
 * @example
 * ```ts
 * // Extending the base error class
 * export class ValidationError extends xJetBaseError {
 *   constructor(message: string) {
 *     super(message);
 *     this.name = 'ValidationError';
 *   }
 * }
 * ```
 *
 * @since 1.0.0
 */
import { serializesError } from '@components/object.component';

export abstract class xJetBaseError extends Error {
    /**
     * Creates a new instance of the base error class.
     *
     * @param message - The error message describing the problem.
     * @param name - Optional name for the error class; defaults to `'xJetError'`.
     *
     * @remarks
     * This constructor ensures the correct prototype chain for proper `instanceof` checks.
     * It also captures the stack trace if supported by the runtime environment.
     *
     * @example
     * ```ts
     * class MyError extends xJetBaseError {}
     * throw new MyError('Something went wrong');
     * ```
     *
     * @since 1.0.0
     */

    protected constructor(message: string, name: string = 'xJetError') {
        super(message);

        // Ensure correct prototype chain (important for `instanceof`)
        Object.setPrototypeOf(this, new.target.prototype);
        this.name = name;

        if (Error.captureStackTrace) {
            Error.captureStackTrace(this, this.constructor);
        }
    }

    /**
     * Serializes the xJetError instance to a plain object suitable for JSON conversion.
     *
     * @returns A plain object containing all properties of the error instance
     *
     * @remarks
     * This method creates a serializable representation of the error by copying all own
     * enumerable properties to a new object. It also explicitly ensures that critical properties
     * like name, message, and stack are included in the result, even if they are non-enumerable
     * in the original error object.
     *
     * The resulting object can be used with JSON.stringify() to create a complete JSON
     * representation of the error, which is useful for logging, error reporting, and
     * transmitting error details across process boundaries.
     *
     * @example
     * ```ts
     * const error = new xJetError('Expected value to be truthy');
     * const serialized = error.toJSON();
     * const json = JSON.stringify(serialized);
     *
     * // Later, this can be used for error reporting
     * console.error(json);
     * ```
     *
     * @since 1.0.0
     */

    toJSON(): Record<string, unknown> {
        return serializesError(this);
    }
}
